import { useState } from 'react';
import { css } from '@emotion/react';
import { capitalize } from 'lodash-es';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Button, Heading, SwitchChipGroup, Text } from '@frontend/design-system';
import { insuranceBenefitSummaryAmountStyle } from '../styles';
import { BenefitLimitOverview } from '../utils/types';
import { calculateTuuthFairySpentAmount, hasInNetworkData, hasOutNetworkData, toTitleCase } from '../utils/utils';
import { DetailText } from './detail-text';

interface Props {
  benefitDetails: BenefitLimitOverview | undefined;
}

type NetworkType = 'in-network' | 'out-network';

export const InsuranceBenefitSummary = ({ benefitDetails }: Props) => {
  const { t } = useTranslation();
  const defaultNetworkType = hasInNetworkData(benefitDetails) ? 'in-network' : 'out-network';
  const [networkType, setNetworkType] = useState<NetworkType>(defaultNetworkType);
  if (!benefitDetails) {
    return null;
  }
  const { maximum, deductible } = benefitDetails;
  const { inNetwork: inNetworkMaximum, outOfNetwork: outOfNetworkMaximum } = maximum;
  const { inNetwork: inNetworkDeductible, outOfNetwork: outOfNetworkDeductible } = deductible;

  const isInNetwork = networkType === 'in-network';
  const selectedNetworkDetailsDeductible = isInNetwork ? inNetworkDeductible : outOfNetworkDeductible;
  const selectedNetworkDetailsMaximum = isInNetwork ? inNetworkMaximum : outOfNetworkMaximum;

  const spentAmountMaximum = calculateTuuthFairySpentAmount(selectedNetworkDetailsMaximum);
  const spentAmountDeductible = calculateTuuthFairySpentAmount(selectedNetworkDetailsDeductible);

  return (
    <>
      <section
        css={{ padding: theme.spacing(2, 3, 1), display: 'flex', flexDirection: 'column', gap: theme.spacing(4) }}
      >
        <div
          css={{
            marginBottom: theme.spacing(1),
          }}
        >
          <SwitchChipGroup
            singleSelect
            defaultValue={defaultNetworkType}
            setValue={(val) => {
              setNetworkType(val.toString() as NetworkType);
            }}
            value={[networkType]}
          >
            {hasInNetworkData(benefitDetails) && (
              <SwitchChipGroup.Option value='in-network'>{t('In-Network')}</SwitchChipGroup.Option>
            )}
            {hasOutNetworkData(benefitDetails) && (
              <SwitchChipGroup.Option value='out-network'>{t('Out-Network')}</SwitchChipGroup.Option>
            )}
          </SwitchChipGroup>
        </div>
        <div css={insuranceBenefitSummaryAmountStyle}>
          {selectedNetworkDetailsMaximum && (
            <>
              <header className='benefit-limits-header'>
                <Heading level={3}>{t('Maximum')}</Heading>
              </header>
              <dl css={listStyle}>
                <div>
                  <Text as='dt'>{t('Vertical')}</Text>
                  <DetailText value={toTitleCase(selectedNetworkDetailsMaximum?.category)} />
                </div>
                <div>
                  <Text as='dt'>{t('Amount')}</Text>
                  <p className='total-amount'>
                    <span className='spent-amount'>{'$' + spentAmountMaximum}</span>
                    {`/$${Math.floor((selectedNetworkDetailsMaximum.amount || 0) / 100)}`}
                  </p>
                </div>
                <div>
                  <Text as='dt'>{t('Network Name')}</Text>
                  <DetailText value={toTitleCase(selectedNetworkDetailsMaximum?.networkName, ' ')} required />
                </div>
                <div>
                  <Text as='dt'>{t('Coverage Level')}</Text>
                  <DetailText value={capitalize(selectedNetworkDetailsMaximum?.coverageLevel)} required />
                </div>
              </dl>
            </>
          )}
          {selectedNetworkDetailsDeductible && (
            <>
              <header className='benefit-limits-header'>
                <Heading level={3}>{t('Deductible')}</Heading>
              </header>
              <dl css={listStyle}>
                <div>
                  <Text as='dt'>{t('Vertical')}</Text>
                  <DetailText value={toTitleCase(selectedNetworkDetailsDeductible?.category)} required />
                </div>
                <div>
                  <Text as='dt'>{t('Amount')}</Text>
                  <p className='total-amount'>
                    <span className='spent-amount'>{'$' + spentAmountDeductible}</span>
                    {`/$${Math.floor((selectedNetworkDetailsDeductible.amount || 0) / 100)}`}
                  </p>
                </div>
                <div>
                  <Text as='dt'>{t('Network Name')}</Text>
                  <DetailText value={toTitleCase(selectedNetworkDetailsDeductible?.networkName, ' ')} required />
                </div>
                <div>
                  <Text as='dt'>{t('Coverage Level')}</Text>
                  <DetailText value={capitalize(selectedNetworkDetailsDeductible?.coverageLevel)} required />
                </div>
              </dl>
            </>
          )}
        </div>
        <Button
          variant='tertiary'
          disabled //TODO: This button will be activated after the report generation implementation is completed
          onClick={() => {}}
        >
          {t('Show More')}
        </Button>
      </section>
    </>
  );
};

const listStyle = css`
  margin-bottom: ${theme.spacing(3)};
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${theme.spacing(2)};
  grid-auto-flow: dense;
  > div {
    display: flex;
    flex-direction: column;

    dt {
      margin-right: ${theme.spacing(4)};
      font-size: ${theme.fontSize(12)};
      margin-bottom: ${theme.spacing(0.5)};
      color: ${theme.font.colors.subdued};
    }
  }
`;
InsuranceBenefitSummary.displayName = 'InsuranceBenefitSummary';
